import React, { useEffect, useState } from "react";
import { DetailSectionComponent } from "../../../components/DetailSectionComponent";
import { HeaderProps } from "../../../components/Header";
import { useTranslation } from "react-i18next";
import { useRouteNavigator } from "../../../routes/RoutesHelper";
import { Stack } from "@mui/material";
import { Button, useSpacing } from "@surya-digital/leo-reactjs-material-ui";
import { observer } from "mobx-react";
import { Plus } from "lucide-react";
import { useSignInStore } from "../../store/Hooks";
import { UserPrivileges } from "../../store/user/UserPrivileges";
import { navigateOrGetNetworkErrorPage } from "../../../utility/navigateOrGetNetworkErrorPage";
import { useNetworkingErrorStore } from "../../networking/store/hooks";
import { AllowedDomainsPageContent } from "./contents/AllowedDomainsPageContent";
import {
  useAllowedDomainDetailsStore,
  useAllowedDomainsStore,
  useCreateAllowedDomainStore,
  useDeleteAllowedDomainStore,
  useUpdateAllowedDomainStore,
} from "../stores/Hooks";
import {
  AllowedDomainDialog,
  AllowedDomainDialogMode,
} from "../components/dialogs/AllowedDomainDialog";
import { ConfirmAllowedDomainDeletionDialog } from "../components/dialogs/ConfirmAllowedDomainDeletionDialog";
import { UnexpectedErrorDialog } from "../../../components/dialogs/UnexpectedErrorDialog";
import { Instance } from "mobx-state-tree";
import { AllowedDomainModel } from "../models/AllowedDomainModel";

export const AllowedDomainsPage: React.FC = observer(() => {
  const [openAllowedDomainDialog, setOpenAllowedDomainDialog] = useState(false);
  const [allowedDomainDialogMode, setAllowedDomainDialogMode] =
    useState<AllowedDomainDialogMode>("create");
  const [
    openConfirmDeleteAllowedDomainDialog,
    setOpenConfirmDeleteAllowedDomainDialog,
  ] = useState(false);
  const [openStaleAllowedDomainDialog, setOpenStaleAllowedDomainDialog] =
    useState(false);

  const { t } = useTranslation();
  const translate = t as unknown as (key: string) => string;
  const spacing = useSpacing();
  const navigateTo = useRouteNavigator();
  const signInStore = useSignInStore();
  const errorStore = useNetworkingErrorStore();
  const allowedDomainsStore = useAllowedDomainsStore();
  const allowedDomainDetailsStore = useAllowedDomainDetailsStore();
  const createAllowedDomainStore = useCreateAllowedDomainStore();
  const updateAllowedDomainStore = useUpdateAllowedDomainStore();
  const deleteAllowedDomainStore = useDeleteAllowedDomainStore();

  const hasAllowedDomainsPrivilege = signInStore.isPrivilegeGranted(
    UserPrivileges.processAllowedDomains,
  );

  useEffect(() => {
    if (!hasAllowedDomainsPrivilege) {
      navigateTo.pageNotFound();
    }
  }, [hasAllowedDomainsPrivilege]);

  useEffect(
    () => () => {
      allowedDomainsStore.resetStore();
      allowedDomainDetailsStore.resetStore();
      errorStore.removeError();
    },
    [],
  );

  const resetDialogStores = (): void => {
    createAllowedDomainStore.resetStore();
    updateAllowedDomainStore.resetStore();
    deleteAllowedDomainStore.resetStore();
  };

  const handleAddDomainButtonClick = (): void => {
    allowedDomainDetailsStore.resetStore();
    createAllowedDomainStore.resetStore();
    setAllowedDomainDialogMode("create");
    setOpenAllowedDomainDialog(true);
  };

  const handleEditAllowedDomain = (
    allowedDomain: Instance<typeof AllowedDomainModel>,
  ): void => {
    allowedDomainDetailsStore.setAllowedDomain(
      allowedDomain.id,
      allowedDomain.domainName.name,
      allowedDomain.domain.name,
    );
    setAllowedDomainDialogMode("edit");
    setOpenAllowedDomainDialog(true);
  };

  const handleDeleteAllowedDomain = (
    allowedDomain: Instance<typeof AllowedDomainModel>,
  ): void => {
    deleteAllowedDomainStore.setAllowedDomain(
      allowedDomain.id,
      allowedDomain.domainName.name,
    );
    setOpenConfirmDeleteAllowedDomainDialog(true);
  };

  const handleCloseAllowedDomainDialog = (): void => {
    setOpenAllowedDomainDialog(false);
    resetDialogStores();
  };

  const handleCloseDeleteDialog = (): void => {
    setOpenConfirmDeleteAllowedDomainDialog(false);
    deleteAllowedDomainStore.resetStore();
  };

  const handleStaleAllowedDomain = async (): Promise<void> => {
    setOpenAllowedDomainDialog(false);
    setOpenConfirmDeleteAllowedDomainDialog(false);
    resetDialogStores();
    await allowedDomainsStore.refreshCurrentPage();
    setOpenStaleAllowedDomainDialog(true);
  };

  const mainHeaderProps: HeaderProps = {
    title: translate("emailCycles.allowedDomainsTitle"),
    prefersLargeTitle: true,
    backButtonCallback: navigateTo.project,
    actionElement: hasAllowedDomainsPrivilege ? (
      <Button
        label={translate("emailCycles.allowedDomainsAddDomain")}
        name="addAllowedDomain"
        size="medium"
        variant="filled"
        icon={<Plus />}
        onClick={handleAddDomainButtonClick}
      />
    ) : undefined,
  };

  const errorPage = navigateOrGetNetworkErrorPage(
    errorStore.error,
    navigateTo,
    errorStore.removeError,
  );

  if (errorPage) {
    errorStore.removeError();
    return errorPage;
  }

  if (!hasAllowedDomainsPrivilege) {
    return <></>;
  }

  return (
    <>
      <DetailSectionComponent
        mainHeaderProps={mainHeaderProps}
        isOverflowHidden={false}
        isChildHeightVariable={false}
        isManageUsersLogoVisible={true}
        isAuditLogsLogoVisible={true}
        isEmailTemplatesLogoVisible={true}
        isAllowedDomainsLogoVisible={false}
      >
        <Stack
          justifyContent="start"
          alignItems="center"
          height="100%"
          width="100%"
          padding={spacing.spaceXL}
        >
          <AllowedDomainsPageContent
            onClickAddDomainButton={handleAddDomainButtonClick}
            onEditAllowedDomain={handleEditAllowedDomain}
            onDeleteAllowedDomain={handleDeleteAllowedDomain}
          />
        </Stack>
      </DetailSectionComponent>

      <AllowedDomainDialog
        open={openAllowedDomainDialog}
        mode={allowedDomainDialogMode}
        onClose={handleCloseAllowedDomainDialog}
        onDomainNotFound={() => void handleStaleAllowedDomain()}
      />

      <ConfirmAllowedDomainDeletionDialog
        open={openConfirmDeleteAllowedDomainDialog}
        onClose={handleCloseDeleteDialog}
        onDomainNotFound={() => void handleStaleAllowedDomain()}
      />

      {openStaleAllowedDomainDialog && (
        <UnexpectedErrorDialog
          message={translate(
            "emailCycles.allowedDomainsDomainNotFoundErrorMessage",
          )}
          onClickDone={() => setOpenStaleAllowedDomainDialog(false)}
        />
      )}
    </>
  );
});
